import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  QuestionnaireQuestionsDocument,
  SaveQuestionnaireResultsDocument,
} from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";
import {
  AssessmentFlow,
  type AssessmentItem,
} from "@/components/grow/AssessmentFlow";

// Questionnaire (QNR_*) adapter for the shared AssessmentFlow template: loads
// a questionnaire's active questions, maps each question's own answer choices
// to template options and submits the chosen choice ids in one batch (no
// autosave - genesis logs element scores per result row, so partial saves
// would publish half-done scores). Wrappers provide context: meta, reveal
// artwork and where to go after submit.

type SaveStatus = "idle" | "saving" | "error";

type Props = {
  questionnaireId: string;
  name?: string | null;
  instructions?: string | null;
  eyebrow: React.ReactNode;
  revealSrc?: string | null;
  onSubmitted: () => void;
};

export function QuestionnaireRunner({
  questionnaireId,
  name,
  instructions,
  eyebrow,
  revealSrc,
  onSubmitted,
}: Props) {
  const intl = useIntl();

  // questionId → chosen answerChoiceId
  const [answers, setAnswers] = useState<Record<string, string | undefined>>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const { data, loading } = useQuery(QuestionnaireQuestionsDocument, {
    variables: { questionnaireId },
    skip: !questionnaireId,
    fetchPolicy: "cache-and-network",
  });
  const [save] = useMutation(SaveQuestionnaireResultsDocument);

  const questions = useMemo(
    () =>
      (data?.questionnaireQuestions ?? [])
        .filter((q) => q.status === "ACTIVE")
        .slice()
        .sort((a, b) => a.position - b.position),
    [data?.questionnaireQuestions],
  );

  const items: AssessmentItem[] = useMemo(
    () =>
      questions.map((q) => ({
        id: q.qnrQuestionId,
        text: q.question,
        options: q.answerChoices
          .filter((c) => c.status === "ACTIVE")
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((c, i) => ({
            key: c.qnrAnswerChoiceId,
            title: i + 1,
            label: c.answer,
          })),
      })),
    [questions],
  );

  const total = items.length;
  const answeredCount = items.reduce((n, it) => (answers[it.id] ? n + 1 : n), 0);
  const complete = total > 0 && answeredCount === total;

  // Reveal: the artwork starts hidden + blurred and sharpens one answered
  // question at a time, mirroring the self-assessment.
  const revealProgress = total > 0 ? answeredCount / total : 0;

  async function submit() {
    if (!complete || saveStatus === "saving") return;
    setSaveStatus("saving");
    const results = items
      .map((it) => answers[it.id])
      .filter((cid): cid is string => Boolean(cid))
      .map((qnrAnswerChoiceId) => ({ qnrAnswerChoiceId }));
    try {
      const res = await save({ variables: { input: { results } } });
      if (!res.data?.saveQuestionnaireResults) throw new Error("saveQuestionnaireResults=false");
      onSubmitted();
    } catch {
      logApplicationFailure("grow.save-questionnaire");
      setSaveStatus("error");
    }
  }

  return (
    <>
      <header className="mb-8 flex items-start justify-between gap-6">
        <div>
          <p className="text-xs tracking-wide text-ink/55">{eyebrow}</p>
          <h1 className="mt-1 text-3xl font-semibold">
            {name ?? intl.formatMessage({ id: "grow.questionnaire.fallbackName", defaultMessage: "Questionnaire" })}
          </h1>
          {instructions ? <p className="mt-2 text-sm text-ink/55">{instructions}</p> : null}
        </div>
        {revealSrc ? (
          <div className="relative hidden aspect-[16/10] w-64 shrink-0 overflow-hidden rounded-xl bg-ink/5 ring-1 ring-black/10 sm:block">
            <img
              src={revealSrc}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover transition-all duration-700 ease-out"
              style={{ opacity: revealProgress, filter: `blur(${20 * (1 - revealProgress)}px)` }}
            />
          </div>
        ) : null}
      </header>

      {loading && total === 0 ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : total === 0 ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="grow.questionnaire.noQuestions" defaultMessage="No questions in this questionnaire." />
        </p>
      ) : (
        <AssessmentFlow
          items={items}
          selected={answers}
          onSelect={(id, key) => setAnswers((prev) => ({ ...prev, [id]: key }))}
          itemLabel={(index) => (
            <FormattedMessage
              id="grow.questionnaire.questionLabel"
              defaultMessage="Question {position}"
              values={{ position: index }}
            />
          )}
          saving={saveStatus === "saving"}
          saveError={saveStatus === "error"}
          submitLabel={<FormattedMessage id="grow.questionnaire.submit" defaultMessage="Submit" />}
          onSubmit={() => void submit()}
        />
      )}
    </>
  );
}
